import { toTemplateTag } from "./fieldsMapper";
import type { QueryField } from "./types";

/** Floor for an empty grid and for short-tag queries, so the header and the
 *  copy button never crowd each other. */
export const MERGE_FIELD_MIN_WIDTH = 250;

/** Ceiling for pathologically long tags. The column is pinned left alongside
 *  Name (200+) and Variant (115), and pinned columns don't scroll — without a
 *  cap, one long alias-derived tag could push the pinned region past the
 *  viewport and make every unpinned column unreachable. A capped tag still
 *  ellipsizes, but the copy button always yields the full value. */
export const MERGE_FIELD_MAX_WIDTH = 600;

/** Tags render in a fixed-advance monospace face (`mergeFieldText`), so the
 *  widest tag's pixel width is derivable from the row data. Measured off the
 *  rendered grid rather than taken from the nominal 0.6em advance, which came
 *  up a couple of characters short.
 *  AG Grid's `autoSizeColumns` is not an option here: it measures only the rows
 *  currently rendered, so tags below the virtualization window would still clip. */
const CHARACTER_WIDTH = 7.6;

/** Cell padding, plus the gap and copy button on the trailing edge. Generous
 *  on purpose — the cost of overshooting is some whitespace before the copy
 *  button, while undershooting ellipsizes a tag the user needs to read. */
const CELL_CHROME_WIDTH = 130;

const longestTagLength = (fields: ReadonlyArray<QueryField>): number =>
  fields.reduce(
    (longest, field) =>
      Math.max(longest, toTemplateTag(field.mergeField, field.isTransactionLevel).length),
    0,
  );

/** Width that shows every row's merge field in full (up to the pinned-region
 *  ceiling), so the column doesn't ellipsize a tag the user needs to read or
 *  copy. */
export const computeMergeFieldColumnWidth = (fields: ReadonlyArray<QueryField>): number =>
  Math.min(
    MERGE_FIELD_MAX_WIDTH,
    Math.max(
      MERGE_FIELD_MIN_WIDTH,
      Math.ceil(longestTagLength(fields) * CHARACTER_WIDTH) + CELL_CHROME_WIDTH,
    ),
  );
